import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as storage from '@src/common/storage';
import { useAppSelector } from '@src/core/hooks';
import { publicApiTokenSelector } from '@src/stores/app';
import type { FormInputValues } from '@src/domains/orders';

import {
  MAX_DRAFT_CHUNKS,
  chunkFormInputsDraft,
  formInputsDraftKey,
  parseFormInputsDraft,
} from '@src/features/orders/form-inputs/core/formInputsDraft';

/**
 * Only the draft carries the buyer's documents, so only it tightens the cookie. The
 * shared cookies — buyer token, cart id — keep the attributes they always had, since
 * `sameSite` also governs whether they survive the return from a payment redirect.
 *
 * `lax` rather than `strict` for the same reason, and `secure` only under https so the
 * cookie is not dropped on a local http origin.
 */
const DRAFT_COOKIE_ATTRIBUTES = {
  sameSite: 'lax',
  secure: window.location.protocol === 'https:',
} as const;

type UseFormInputsDraftParams = {
  orderId?: string;
  expiresAt?: string | null;
};

/**
 * Local draft of the filling. The submit is a single call on "continue", since the
 * backend takes no partial payload, so what survives a refresh is this cookie.
 */
export const useFormInputsDraft = (params: UseFormInputsDraftParams) => {
  const { orderId, expiresAt } = params;
  const publicApiKey = useAppSelector(publicApiTokenSelector);

  const [values, setValues] = useState<FormInputValues>({});
  const hydratedOrderId = useRef<string | null>(null);
  // Mirrors the state so the cookie write can happen outside the updater, which React
  // may call more than once for a single update.
  const valuesRef = useRef<FormInputValues>({});

  /**
   * Hydration cannot live in the `useState` initializer: it runs once, and on the first
   * render the order is still loading, so after a refresh `orderId` only shows up when
   * the request returns. Without this the draft is written but never read back.
   */
  useEffect(() => {
    if (!orderId || hydratedOrderId.current === orderId) {
      return;
    }

    hydratedOrderId.current = orderId;
    const chunks = Array.from({ length: MAX_DRAFT_CHUNKS }, (_, index) =>
      storage.getValue(publicApiKey, formInputsDraftKey(orderId, index)),
    );
    const stored = parseFormInputsDraft(chunks);

    // What the buyer already typed wins over the cookie: they may have started filling
    // before the order finished loading.
    setValues((current) => {
      const next = { ...stored, ...current };
      valuesRef.current = next;

      return next;
    });
  }, [orderId, publicApiKey]);

  /**
   * An invalid or already past date produces a cookie the browser drops on the spot,
   * silently. A session cookie is better there: it at least survives a refresh.
   */
  const expires = useMemo(() => {
    if (!expiresAt) {
      return undefined;
    }

    const parsed = new Date(expiresAt);

    return Number.isNaN(parsed.getTime()) || parsed.getTime() <= Date.now() ? undefined : parsed;
  }, [expiresAt]);

  const saveValues = useCallback(
    (patch: FormInputValues) => {
      const next = { ...valuesRef.current, ...patch };

      valuesRef.current = next;
      setValues(next);

      const chunks = orderId ? chunkFormInputsDraft(next) : null;

      if (!orderId || !chunks) {
        return;
      }

      chunks.forEach((chunk, index) =>
        storage.setValue(publicApiKey, formInputsDraftKey(orderId, index), chunk, expires, DRAFT_COOKIE_ATTRIBUTES),
      );

      // When the draft shrinks, a leftover chunk would make the read append garbage
      // to the end of the JSON, since the read stops at the first missing chunk.
      for (let index = chunks.length; index < MAX_DRAFT_CHUNKS; index += 1) {
        storage.removeValue(publicApiKey, formInputsDraftKey(orderId, index));
      }
    },
    [expires, orderId, publicApiKey],
  );

  const clear = useCallback(() => {
    if (orderId) {
      for (let index = 0; index < MAX_DRAFT_CHUNKS; index += 1) {
        storage.removeValue(publicApiKey, formInputsDraftKey(orderId, index));
      }
    }

    valuesRef.current = {};
    setValues({});
  }, [orderId, publicApiKey]);

  return { values, saveValues, clear };
};

export default useFormInputsDraft;
